import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Building2, Boxes, Ruler, Palette, Layers, ClipboardList, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const SERVICES = [
  { id: "architectural", icon: Building2, title: "Architectural Design", tag: "Full design service", desc: "Residential, commercial, and mixed-use design — from concept through construction documents. Every drawing prepared by licensed architects.", bullets: ["Concept & schematic design", "Design development & DD sets", "Construction documents", "Permit-ready deliverables"] },
  { id: "bim", icon: Boxes, title: "BIM & Revit Modeling", tag: "LOD 100 – 500", desc: "Federated BIM models used by builders, structural engineers, and MEP consultants to eliminate on-site RFIs and clashes.", bullets: ["Revit LOD 300–500 modeling", "Clash detection", "Quantity takeoffs", "Point cloud to BIM"] },
  { id: "cad", icon: Ruler, title: "CAD Drafting", tag: "AutoCAD certified", desc: "High-precision 2D drafting for architects, contractors, and homeowners. Legacy blueprint digitization and detailed shop drawings.", bullets: ["2D architectural drafting", "Structural & MEP drawings", "Legacy blueprint scanning", "As-built documentation"] },
  { id: "3d", icon: Palette, title: "3D Rendering", tag: "Photorealistic visuals", desc: "Stunning photoreal renders and virtual walkthroughs to help clients and buyers experience the design before it's built.", bullets: ["Interior & exterior renders", "360° virtual tours", "Animated fly-throughs", "VR-ready assets"] },
  { id: "modeling", icon: Layers, title: "3D Modeling", tag: "Precision assets", desc: "Custom 3D models — architectural, product, furniture — built for rendering, animation, and BIM integration.", bullets: ["Architectural models", "Furniture & product models", "Landscape modeling", "Optimized topology"] },
  { id: "consult", icon: ClipboardList, title: "Design Consulting", tag: "Expert advice", desc: "One-off consulting sessions with our senior architects — plan reviews, site suitability studies, sustainability audits.", bullets: ["Site suitability studies", "Plan review & optimization", "Passive design consulting", "Code compliance audits"] },
];

const Services = () => {
  React.useEffect(() => { document.title = "Services — Architectural Design, BIM, CAD, 3D Rendering | Dream Home Engineers"; }, []);

  return (
    <Layout>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-24">
        <div className="max-w-3xl">
          <div className="overline text-accent">Our Services</div>
          <h1 className="font-display text-5xl lg:text-7xl mt-4 leading-[0.95] tracking-tighter">
            Beyond the plan library.
          </h1>
          <p className="mt-8 text-lg text-muted-foreground">
            Since 2012, Dream Home Engineers has served homeowners, developers, and design firms across 40+ countries — from stock house plans to full architectural design, BIM, CAD drafting, and photorealistic 3D visualization.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 lg:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map(({ id, icon: Icon, title, tag, desc, bullets }) => (
            <div key={id} id={id} className="border border-border p-8 flex flex-col" data-testid={`service-${id}`}>
              <div className="w-12 h-12 flex items-center justify-center bg-primary text-primary-foreground">
                <Icon size={20} />
              </div>
              <div className="overline text-accent mt-6">{tag}</div>
              <h2 className="font-display text-3xl mt-2 leading-tight">{title}</h2>
              <p className="mt-4 text-sm text-muted-foreground">{desc}</p>
              <ul className="mt-6 space-y-2 text-sm">
                {bullets.map((b) => <li key={b} className="flex gap-2"><span className="text-accent">·</span>{b}</li>)}
              </ul>
              <Link to="/contact" className="mt-auto pt-8">
                <Button variant="ghost" className="rounded-none px-0 hover:bg-transparent hover:text-accent text-accent">
                  Request a quote <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-primary text-primary-foreground">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24 text-center">
          <div className="overline text-accent">Ready to discuss your project?</div>
          <h2 className="font-display text-4xl lg:text-6xl mt-4 leading-tight">Let's build something worth drawing.</h2>
          <Link to="/contact">
            <Button size="lg" className="rounded-none bg-accent hover:bg-accent/90 mt-10 h-14 px-10 text-base text-accent-foreground">
              Get in touch <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Services;
